/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  APIRoleResponseInterface,
  RequestRoleInterFace,
  RoleInitialProperties,
  RoleInterFace,
} from '../../interface/role-management.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import roleApi from '../../utils/api/roles';

import { setNotificationMessage } from './NotificationManagementSlice';

// a thunk to fetch users
export const fetchAllRoles = createAsyncThunk<APIRoleResponseInterface, RequestRoleInterFace>(
  'role-management/fetchAllRoles',
  async (data: RequestRoleInterFace, { dispatch }) => {
    console.log('fetch-roles______');
    const response = await roleApi
      .fetchRoles(removeEmptyKeys(data))
      .then((response1) => response1)
      .catch((error) =>
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message,
            code: error?.response?.data?.code,
            status: true,
          }),
        ),
      );
    return {
      ...response.data,
      results: response.data.results.map((roleData: RoleInterFace) => ({
        ...roleData,
        createdAt: ConvertUTCtoDate(roleData.createdAt || ''),
        id: roleData._id,
      })),
    };
  },
);

export const fetchAllListRoles = createAsyncThunk<{ label: string; value: string }[]>(
  'role-management/fetchAllListRoles',
  async (data, { dispatch }) => {
    try {
      const response = await roleApi.fetchAllRoles();
      return response.data.map((roleData: RoleInterFace) => ({
        ...roleData,
        id: roleData._id,
        label: roleData.name,
        value: roleData._id,
      }));
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Something went wrong!',
          code: error?.response?.data?.code || 200,
          status: true,
        }),
      );
      // Explicitly rethrow or return a rejected promise
      throw error; // Option 1: Rethrow the error for further handling
      // or return Promise.reject(error); // Option 2: Return a rejected promise
    }
  },
);

export const fetchAllPermissions = createAsyncThunk<{ label: string; value: string }[]>(
  'role-management/fetchAllPermissions',
  async (data, { dispatch }) => {
    const response = await roleApi
      .fetchAllPermissions()
      .then((response1) => response1)
      .catch((error) =>
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message,
            code: error?.response?.data?.code,
            status: true,
          }),
        ),
      );
    return response.data.map((permission: string) => ({
      label: permission,
      value: permission,
    }));
  },
);

// a thunk to fetch devices filters
export const fetchRolesFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('role-management/fetchRolesFilters', async (_, { dispatch }) => {
  try {
    const response = await roleApi.fetchRolesFilterList();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: RoleInitialProperties = {
  roles: [],
  rolesList: [],
  permissionsList: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const roleSlice = createSlice({
  name: 'roles-management',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param state
     * @param action
     */
    updateRolesFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param state
     */
    disableRolesFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */ setSortData: (
      state,
      action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>,
    ) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<RoleInitialProperties>) => {
    builder
      .addCase(fetchAllRoles.pending, (state: RoleInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAllRoles.fulfilled,
        (state: RoleInitialProperties, action: PayloadAction<APIRoleResponseInterface>) => {
          state.loading = false;
          state.roles = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAllRoles.rejected, (state: RoleInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchAllListRoles.pending, (state: RoleInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAllListRoles.fulfilled,
        (
          state: RoleInitialProperties,
          action: PayloadAction<{ label: string; value: string }[]>,
        ) => {
          state.loading = false;
          state.rolesList = [...action.payload];
        },
      )
      .addCase(fetchAllListRoles.rejected, (state: RoleInitialProperties) => {
        state.loading = false;
      });
    builder
      .addCase(fetchAllPermissions.pending, (state: RoleInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAllPermissions.fulfilled,
        (
          state: RoleInitialProperties,
          action: PayloadAction<{ label: string; value: string }[]>,
        ) => {
          state.loading = false;
          state.permissionsList = [...action.payload];
        },
      )
      .addCase(fetchAllPermissions.rejected, (state: RoleInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchRolesFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchRolesFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchRolesFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const { updateRolesFilters, disableRolesFilterApplied, setSortData } = roleSlice.actions;
// Export the reducer
export default roleSlice.reducer;
